import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { StatusBadge } from "./ui";
import { ChevronRight, ArrowLeft } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

const TYPE_LABEL = {
  main_bin: "Main Consignment Bin", regional: "Regional Warehouse",
  fitment_centre: "Fitment Centre", technician: "In-house Technician", unknown: "Unassigned",
};

export function StockDrillDialog({ open, status, region, onClose }) {
  const [locations, setLocations] = useState(null);
  const [selectedLoc, setSelectedLoc] = useState(null);
  const [units, setUnits] = useState(null);

  useEffect(() => {
    if (!open || !status) return;
    setSelectedLoc(null); setUnits(null); setLocations(null);
    const q = new URLSearchParams({ status });
    if (region) q.set("region", region);
    api.get(`/dashboard/drill?${q.toString()}`).then((r) => setLocations(r.data));
  }, [open, status, region]);

  const openLocation = async (loc) => {
    setSelectedLoc(loc);
    setUnits(null);
    const { data } = await api.get(`/units?status=${status}&location_id=${loc.location_id}`);
    setUnits(data);
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-[#0B2545]">
            {selectedLoc && (
              <button data-testid="drill-back" onClick={() => { setSelectedLoc(null); setUnits(null); }} className="text-slate-400 hover:text-[#00857C]">
                <ArrowLeft className="h-4 w-4" />
              </button>
            )}
            {region && <span className="text-sm font-semibold">{region}</span>}
            {region && <ChevronRight className="h-4 w-4 text-slate-300" />}
            {status && <StatusBadge status={status} />}
            {selectedLoc && (
              <>
                <ChevronRight className="h-4 w-4 text-slate-300" />
                <span className="text-sm font-semibold">{selectedLoc.location_name}</span>
              </>
            )}
          </DialogTitle>
        </DialogHeader>

        {!selectedLoc ? (
          locations === null ? (
            <div className="py-8 text-center text-sm text-slate-400">Loading…</div>
          ) : locations.length === 0 ? (
            <div className="py-8 text-center text-sm text-slate-400">No units in this status.</div>
          ) : (
            <div data-testid="drill-locations" className="divide-y divide-slate-100">
              {locations.map((l) => (
                <button
                  key={l.location_id || "unassigned"}
                  data-testid={`drill-loc-${l.location_id}`}
                  onClick={() => openLocation(l)}
                  className="w-full flex items-center justify-between py-3 px-1 hover:bg-slate-50 text-left"
                >
                  <div>
                    <div className="font-medium text-[#0B2545]">{l.location_name}</div>
                    <div className="text-xs text-slate-400">{TYPE_LABEL[l.location_type] || l.location_type} · {l.region}</div>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="font-head text-lg font-bold text-[#00857C]">{l.count}</span>
                    <ChevronRight className="h-4 w-4 text-slate-300" />
                  </div>
                </button>
              ))}
            </div>
          )
        ) : (
          units === null ? (
            <div className="py-8 text-center text-sm text-slate-400">Loading…</div>
          ) : units.length === 0 ? (
            <div className="py-8 text-center text-sm text-slate-400">No serials found.</div>
          ) : (
            <div className="overflow-x-auto max-h-[50vh]">
              <table className="w-full text-sm" data-testid="drill-units-table">
                <thead>
                  <tr className="text-left text-[11px] uppercase tracking-wide text-slate-500 border-b border-slate-200">
                    <th className="py-2 pr-3">Serial</th><th className="py-2 pr-3">Product</th>
                    <th className="py-2 pr-3">VBU</th><th className="py-2 pr-3">WO / Channel</th>
                  </tr>
                </thead>
                <tbody>
                  {units.map((u) => (
                    <tr key={u.id} className="border-b border-slate-100">
                      <td className="py-2 pr-3 font-label text-[13px]">{u.serial}</td>
                      <td className="py-2 pr-3 font-medium text-[#0B2545]">{u.product_name}</td>
                      <td className="py-2 pr-3 text-slate-500">{u.vbu_code}</td>
                      <td className="py-2 pr-3 text-slate-500 text-xs">{u.work_order_no ? `${u.work_order_no} · ${u.channel}` : "—"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )
        )}
      </DialogContent>
    </Dialog>
  );
}
